import fs from "node:fs";
import path from "node:path";
import { downloadImage, getFilenameFromUrl, success, TaskState } from "@iflyrpa/share";
import FormData from "form-data";

import { AxiosProxyParams, MockPublish, Toutiao } from "../../types";
import { extractImgTag, replaceImgSrc, TimeFormatter } from "../../utils";
import { Http } from "../../utils/http";
import { reportLogger } from "../../utils/reportLogger";
import { COVER_TYPE, getAddTypeValue, type PublishAction } from "./index";

const GenAB = require("../../utils/ttABEncrypt") as (headers: string, data: string) => string;

interface CommonResponse<T = unknown> {
    data: T;
    proxyInfo?: Record<string, unknown>;
    code: number;
    message: string;
}

const errnoMap: Record<number, string | string[]> = {
    20004020: "图片上传异常，请重新绑定账号后同步。",
    7115: "正文图片和封面图片推荐jpg、png格式。",
    7112: "文章内图片上传失败，请检查文章中的图片后重试。",
    100006: "文章同步异常，请重新绑定账号后重试。",
    3101: [
        "今日头条号发布次数已达上限，请明天再试。",
        "头条账号状态异常，需前往头条号完成实名认证。",
        "头条账号状态异常，资料审核未通过，请前往头条号查看。",
    ],
    2007: "文章发布失败，声明原创要求正文字数不少于 300 字，请修改后重试。",
    2010: "无头条广告权限,请修改后重试。",
    3103: "今日头条号发布次数已达上限，请明天再试。",
    1053: "正文图片和封面图片推荐jpg、png格式。",
    1050: "图片上传失败,请重试上传。",
    2005: "账号无【申明原创】权限，请关闭申明后重试。",
    100004: "用户未登录或登陆已过期，请重新登陆后重试。",
    4029: "标题长度应该在2-30字之间，请修改后重试。",
    3102: "账号已被禁言无法发布文章，请先保存草稿！",
};
const generatorFormData = (data: Record<string, unknown>) => {
    return Object.entries(data)
        .map(([key, value]) => {
            const encodedKey = encodeURIComponent(key);
            const encodedValue = encodeURIComponent(String(value));
            return `${encodedKey}=${encodedValue}`;
        })
        .join("&");
};
const get3101DetailError = (errorList: string[], message: string, saveType: string) => {
    let error = "";
    if (message?.includes("当日发文")) {
        error = errorList[0];
    } else if (message?.includes("实名认证")) {
        error = errorList[1];
    } else if (message?.includes("资料审核")) {
        error = errorList[2];
    }
    return error || (saveType === "draft" ? "文章同步异常，请稍后重试。" : "文章发布异常，请稍后重试。");
};

export const mockAction: PublishAction = async (task, params) => {
    const { toutiaoSingleCover, toutiaoMultCover, toutiaoCoverType, toutiaoOriginal, toutiaoExclusive, toutiaoClaim } =
        params.settingInfo;
    const tmpCachePath = task.getTmpPath();
    const headers = {
        cookie: params.cookies.map((it) => `${it.name}=${it.value}`).join(";"),
        origin: "https://mp.toutiao.com",
        referer: "https://mp.toutiao.com/profile_v4/graphic/publish",
    };
    const args: AxiosProxyParams = [
        { headers },
        task.logger,
        params.proxyLoc,
        params.accountId,
        "toutiao", // 业务标识
    ];
    const http = new Http({ headers });
    const proxyHttp = new Http(...args);

    // 添加响应拦截器
    proxyHttp.addResponseInterceptor<CommonResponse>((response) => {
        // 检查响应中的 errno 字段
        if (response && response.data?.code !== 0) {
            let errmsg = "";

            if (Array.isArray(errnoMap[response.data.code])) {
                errmsg = get3101DetailError(
                    errnoMap[response.data.code] as string[],
                    response.data.message,
                    params.saveType,
                );
            } else {
                errmsg =
                    (errnoMap[response.data.code] as string) ||
                    response.config.defaultErrorMsg ||
                    (params.saveType === "draft" ? "文章同步异常，请稍后重试。" : "文章发布异常，请稍后重试。");
            }

            return {
                code: errnoMap[response.data.code] ? 414 : 500,
                message: errmsg,
                data: response.data,
            };
        }
    });

    // 0. 校验登录
    await http.api({
        method: "get",
        url: "https://mp.toutiao.com/mp/agw/creator_center/user_info",
        defaultErrorMsg: `头条号登录状态失效，请重新绑定账号后${params.saveType === "draft" ? "同步" : "发布"}。`,
    });

    const uploadImages = async (images: string[]) => {
        // 下载图片到本地
        return await Promise.all(
            images.map(async (url) => {
                const fileName = getFilenameFromUrl(url);
                const image = await downloadImage(url, path.join(tmpCachePath, fileName));

                // 检查文件大小（头条号限制 20MB）
                const stats = fs.statSync(image);
                const maxSize = 20 * 1024 * 1024; // 20MB
                if (stats.size > maxSize) {
                    throw {
                        code: 414,
                        message: "头条号平台：单张图片不得超过 20MB",
                        data: "",
                    };
                }

                const formData = new FormData();
                formData.append("image", fs.createReadStream(image));

                const response = await http.api<CommonResponse<Toutiao.ImageData>>({
                    method: "post",
                    url: "https://mp.toutiao.com/spice/image?upload_source=20020002&aid=1231&device_platform=web",
                    data: formData,
                    defaultErrorMsg:
                        params.saveType === "draft"
                            ? "图片上传异常，请稍后重试同步。"
                            : "图片上传异常，请稍后重试发布。",
                });

                return response.data;
            }),
        );
    };

    // 1. 提取并上传正文中的图片
    const originContentImages = extractImgTag(params.content);
    const targetContentImages = await uploadImages(originContentImages);
    // 2. 替换正文中的图片
    const content = replaceImgSrc(params.content, (dom) => {
        if (dom.attribs.src) {
            const idx = originContentImages.indexOf(dom.attribs.src.replace(/&amp;/g, "&"));
            dom.attribs.src = targetContentImages[idx].image_url;
            dom.attribs.web_uri = targetContentImages[idx].image_uri;
            dom.attribs.img_width = targetContentImages[idx].image_width.toString();
            dom.attribs.image_height = targetContentImages[idx].image_height.toString();
            dom.attribs.image_type = targetContentImages[idx].image_type.toString();
            dom.attribs.mime_type = targetContentImages[idx].image_mime_type;
        }
        return dom;
    });

    // 3. 上传横版封面
    const coverUrl =
        toutiaoCoverType === "single" ? [toutiaoSingleCover] : toutiaoCoverType === "multiple" ? toutiaoMultCover : [];
    const coverRes = await uploadImages(coverUrl);

    // 4. 构造发布数据
    // const isSingleTitle = params.settingInfo.toutiaoTitleType === "single";
    const location = params.settingInfo.toutiaoLocation;
    const topics = params.settingInfo.toutiaoTopic || [];

    // const multiTitleData = params.settingInfo.subTitles.map((title) => ({
    // 	id: "0",
    // 	visibility_level: 0,
    // 	source: "author",
    // 	old_source: "author",
    // 	text: title,
    // 	version: "author",
    // 	ai_title: "",
    // }));

    const topicContent = topics
        .map(
            (topic) =>
                `<p><a class="tteditor-forum" data-name="${topic.word}" data-id="${topic.id}" data-concern-id="${topic.id}">#${topic.word}#</a ></p >`,
        )
        .join("");

    let sourceData: Record<string, unknown> = {
        source_type: toutiaoClaim ? toutiaoClaim.type : 5, // 老用户默认为：个人观点，仅供参考
    };
    if (toutiaoClaim && toutiaoClaim.type === 1) {
        if (!toutiaoClaim?.source_author_uid) return { code: 500, message: "请填写作者ID", data: "" };
        sourceData = {
            ...sourceData,
            source_author_uid: toutiaoClaim.source_author_uid,
            time_format: toutiaoClaim.time_format,
            position: toutiaoClaim.position,
        };
    }
    const extraData = {
        content_word_cnt: params.settingInfo.cntWord,
        // is_multi_title: isSingleTitle ? 0 : 1,
        // sub_titles: isSingleTitle ? [] : multiTitleData,
        gd_ext: {
            entrance: "",
            from_page: "publisher_mp",
            enter_from: "PC",
            device_platform: "mp",
            is_message: 0,
        },
        tuwen_wtt_trans_flag: params.settingInfo?.toutiaoTransWtt ? "2" : "0",
        info_source: sourceData,
        ...(location
            ? {
                  manual_selected_city: JSON.stringify({
                      city: location.label,
                      city_code: location.value,
                  }),
              }
            : null),
        ...(params.settingInfo?.toutiaoCollectionId
            ? { want_join_collection_id: params.settingInfo.toutiaoCollectionId }
            : null),
    };

    const publishData = {
        article_type: 0,
        source: 0,
        search_creation_info: '{"searchTopOne": 0, "abstract": "", "clue_id": ""}',
        title_id: "",
        educluecard: "",
        origin_debut_check_pgc_normal: 1,
        is_fans_article: 0,
        govern_forward: 0,
        praise: 0,
        disable_praise: 0,
        tree_plan_article: 0,
        star_order_id: "",
        star_order_name: "",
        customer_nick_name: "",
        activity_tag: 0,
        trends_writing_tag: 0,
        is_refute_rumor: 0,
        save: params.saveType === "publish" ? 1 : 0,
        timer_status: params.settingInfo.timer ? 1 : 0,
        timer_time: params.settingInfo.timer ? TimeFormatter.format(params.settingInfo.timer) : "",
        title: params.title,
        content: content + topicContent,
        extra: JSON.stringify(extraData),
        mp_editor_stat: toutiaoCoverType === "no" ? "{}" : '{"image": %d}',
        draft_form_data: JSON.stringify({
            coverType: COVER_TYPE[toutiaoCoverType],
        }),
        pgc_feed_covers: JSON.stringify(
            coverRes.map((item) => ({
                id: "",
                url: item.image_url,
                uri: item.image_uri,
                ic_uri: "",
                thumb_widt: item.image_width,
                thumb_heigh: item.image_height,
                extra: {
                    from_content_uri: "",
                    from_content: "0",
                },
            })),
        ),
        article_ad_type: getAddTypeValue(params.settingInfo.toutiaoAd),
        // claim_origin: toutiaoOriginal.includes("original") ? 1 : 0,
        claim_exclusive: toutiaoExclusive ? toutiaoExclusive : toutiaoOriginal?.includes("exclusive") ? 1 : 0, // 首发声明兼容老用户字段
    };

    task._timerRecord.PrePublish = Date.now();

    task.logger.info(`[toutiaoPublish] ${params.saveType === "draft" ? "同步草稿" : "发布文章"}参数`, {
        saveType: params.saveType,
        title: params.title,
        extra: extraData,
        publishData,
    });

    const msToken = params.cookies.find((it) => it.name === "msToken")?.value;
    let publishOption = {};
    if (!msToken) {
        publishOption = {
            method: "post",
            url: "https://mp.toutiao.com/mp/agw/article/publish?source=mp&type=article&aid=1",
            data: publishData,
            headers: {
                "content-type": "application/x-www-form-urlencoded",
            },
            defaultErrorMsg: params.saveType === "draft" ? "文章同步异常，请稍后重试。" : "文章发布异常，请稍后重试。",
        };
    } else {
        const postHeaders = generatorFormData({
            source: "mp",
            type: "article",
            aid: 1231,
            mp_publish_ab_val: 0,
            msToken: msToken,
        });
        const postData = generatorFormData(publishData);
        const a_bogus = GenAB(postHeaders, postData);

        publishOption = {
            method: "post",
            url: `https://mp.toutiao.com/mp/agw/article/publish?${postHeaders}&a_bogus=${a_bogus}`,
            data: postData,
            headers: {
                "content-type": "application/x-www-form-urlencoded",
                "user-agent":
                    "Mozilla/5.0 (X11; Linux x86_64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/134.0.0.0 Safari/537.36",
            },
            defaultErrorMsg: params.saveType === "draft" ? "文章同步异常，请稍后重试。" : "文章发布异常，请稍后重试。",
        };
    }

    const updateTaskState = task.taskStageStore?.update?.bind(task.taskStageStore, task.taskId || "");

    if (MockPublish) {
        const message = `文章模拟发布成功 ${http.proxyInfo}`;
        const data = "123456789";

        await updateTaskState?.({
            state: TaskState.SUCCESS,
            result: {
                response: data,
            },
        });
        return success(data, message);
    }

    const publishResult = await proxyHttp.api<CommonResponse<{ pgc_id: string }>>(publishOption, {
        retries: 3,
        retryDelay: 500,
        timeout: 12 * 1000,
    });

    if (params.saveType !== "draft") {
        reportLogger({
            token: params.huiwenToken || "",
            enverionment: task.enverionment || "development",
            postId: params.articleId,
            eip: proxyHttp.proxyInfo,
            accountId: params.accountId,
            uid: params.uid,
            platform: "toutiao",
            publishParams: publishOption,
        });
    }

    const isSuccess = publishResult.code === 0;
    const data = publishResult.data?.pgc_id;
    const message = publishResult.message || (isSuccess ? "文章发布成功！" : "文章发布失败，未知错误。");

    if (!isSuccess) {
        await updateTaskState?.({
            state: TaskState.FAILED,
            error: message,
        });
        return { code: 414, data, message };
    }

    await updateTaskState?.({
        state: TaskState.SUCCESS,
        result: {
            response: data,
        },
    });
    // 临时返回结束
    return success(publishResult.data.pgc_id, `文章发布成功！${task.debug ? ` ${http.proxyInfo}` : ""}`);
};
